/**
 * Launcher V2 U2a — the "act later at the origin" half of the sentinel gate
 * (see `usage-sidebar-errors.ts`'s module doc). Mounted once at each editing
 * surface that can trigger a save/delete through the "Where is this used"
 * sidebar (`SequencesStep.tsx` — hoisted to the play-level `SequencesStep`
 * per D3, not the per-rung `SequenceEditorShell`; `SequenceCopyPane.tsx`/
 * `PlaybooksConfigPage.tsx` — U2b, already at page level).
 *
 * THIN React wrapper only (D14, coding-standards.md §5): the pure
 * decision-execution helper ({@link runUsageSidebarDecision} and its types)
 * lives in `usage-sidebar-decision-logic.ts` — no React, no `_shared/hooks`,
 * so `bun test`s can target it without dragging `web/app-home/**` into
 * `tsconfig.tests.json`'s program. This file is covered by the vitest
 * sidebar suite, not a bun test.
 *
 * ── ONE-SHOT TAKE, TWO LAYERS OF SAFETY (StrictMode) ──────────────────────
 * `takeUsageSidebarDecision` is itself idempotent (a second call with the
 * same `originKey` returns `null` once the first call has cleared
 * `pending.decision`), which already makes a StrictMode double-invoked
 * effect safe on its own. `runningRef` is the belt-and-suspenders half
 * (coding-standards.md's ref-lifetime rule): it additionally blocks the
 * effect from starting a SECOND async run while an earlier one (whose
 * `takeUsageSidebarDecision` call already succeeded and cleared the
 * decision) is still in flight, and — per that same rule — is reset at the
 * TOP of the mount effect, not only in cleanup, so a genuine remount in prod
 * is not left permanently blocked by a stale `true`.
 *
 * ── CONFLICT RETRY BOUND ───────────────────────────────────────────────────
 * `conflictedRef` tracks "did the immediately-preceding decision at this
 * origin end in a `shared_mapping_conflict`". A first conflict refetches
 * usage fresh and leaves the sidebar open with the new count (the rep picks
 * again); a SECOND consecutive conflict shows the plain terminal message
 * and performs no further retry. Reset to `false` on any success, and when
 * the sidebar closes with nothing pending (a fresh save later must not
 * inherit a stale flag from an unrelated earlier one).
 *
 * ── STALE REFUSAL (plan 2026-09-28-008 U1, R14) ───────────────────────────
 * A 409 `version_conflict` from the handler parks THIS pending stale: the
 * Save panel stays open with the server's sentence and a "Reload to see
 * their change" button, the rep's draft stays on the pending, and nothing
 * refetches until the rep presses Reload (`usage-sidebar-reload.ts`). No
 * toast — the panel shows the sentence
 * (`usage-sidebar-decision-logic.ts#settleUsageSidebarFailure`).
 *
 * ── D2: REQUEST IDENTITY ───────────────────────────────────────────────────
 * `taken.requestId` (minted by `openUsageSidebar`) is captured before the
 * async write starts and re-checked against the store's CURRENT
 * `pending.requestId` before every continuation below — see the inline
 * comment at `myRequestId`'s declaration. `chooseUsageSidebarAction` itself
 * already flips `status` to `'running'` (disabling the footer buttons) the
 * instant a decision is recorded; this hook's job is only to flip it back to
 * `'idle'` on anything short of a clean close.
 *
 * ── D15/D16 (Codex F-B / NEW-REQUEST-REPLACEMENT-DEADLOCK) ─────────────────
 * `isStillCurrent` is now forwarded into `runUsageSidebarDecision` (D15), so
 * the PLAY/LIBRARY handler itself can stop before its own cache/draft/toast
 * continuations, not only this hook's `.then`/`.catch`.
 *
 * `runningRequestIdRef` replaces the old plain boolean `runningRef` (D16):
 * it records WHICH request is currently executing rather than merely
 * "something is". `openUsageSidebar` itself now refuses to replace a
 * `status: 'running'` pending request (the primary deadlock fix — a second
 * request can no longer even open while the first is in flight), but this
 * ref additionally means that once an older request's promise settles and
 * clears the ref, THIS SAME effect re-evaluates on its next run (triggered
 * by the `pending` dependency changing) and consumes any newer, still-
 * unconsumed decision rather than staying blocked by a stale "running" flag
 * left over from the request that just finished.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// The root barrel, not `@clay-run/glaze/toast/toast-queue` — this file is
// NOT covered by `tsconfig.tests.json` (only the pure logic module is), but
// stays consistent with the rest of the launcher v2 tree, which resolves
// Glaze via the root barrel everywhere (see `subtype-badge-colors.ts`'s
// module doc for the underlying `moduleResolution` constraint).
import { addErrorToast } from '@clay-run/glaze';

import { useApi } from '../_shared/hooks';
import { getSequenceUsage, getSegmentationUsage } from './api-client';
import {
  runUsageSidebarDecision,
  settleUsageSidebarFailure,
  USAGE_SIDEBAR_CONFLICT_RETRY_MESSAGE,
  type UsageSidebarDecisionHandlers,
} from './usage-sidebar-decision-logic';
import type { PlaybooksUsageResponse } from './schemas';
import {
  closeUsageSidebar,
  originKeyOf,
  refreshUsageSidebarUsage,
  resetUsageSidebarToIdle,
  selectUsageSidebar,
  takeUsageSidebarDecision,
  useUsageSidebarStore,
} from './usage-sidebar-store';

export { USAGE_SIDEBAR_CONFLICT_RETRY_MESSAGE };
export type { UsageSidebarDecisionHandlers };

type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/**
 * The React wrapper. `originKey` must be {@link originKeyOf}`(origin)` for
 * the SAME origin the caller opens the sidebar from — a mismatch means this
 * hook instance simply never sees a matching decision (by design; see
 * `takeUsageSidebarDecision`'s own doc on why a non-owning surface must
 * never consume someone else's decision).
 */
export function useUsageSidebarDecision(originKey: string, handlers: UsageSidebarDecisionHandlers): void {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const pending = useUsageSidebarStore(selectUsageSidebar);
  // D16 — keyed by requestId, not a plain boolean; see this file's module
  // doc ("D15/D16") for why.
  const runningRequestIdRef = useRef<string | null>(null);
  const conflictedRef = useRef(false);

  // StrictMode-resilient (coding-standards.md, root CLAUDE.md): reset at the
  // TOP of the mount effect, not only in cleanup.
  useEffect(() => {
    runningRequestIdRef.current = null;
  }, []);

  useEffect(() => {
    if (pending === null) {
      conflictedRef.current = false;
      return;
    }
    if (pending.decision === null) return;
    if (originKeyOf(pending.origin) !== originKey) return;
    if (runningRequestIdRef.current === pending.requestId) return; // this exact request is already executing.

    const taken = takeUsageSidebarDecision(originKey);
    if (taken === null) return; // Already consumed — idempotent take (StrictMode double-invoke).

    runningRequestIdRef.current = taken.requestId;
    const fetchUsage = (): Promise<PlaybooksUsageResponse> =>
      queryClient.fetchQuery({
        queryKey: [
          'playbooks',
          taken.target.kind === 'sequence' ? 'sequence-usage' : 'segmentation-usage',
          taken.target.id,
        ],
        queryFn: () =>
          taken.target.kind === 'sequence'
            ? getSequenceUsage(apiFetch as ApiFetch, taken.target.id)
            : getSegmentationUsage(apiFetch as ApiFetch, taken.target.id),
        staleTime: 0,
      });

    // D2 — this request's own identity, captured at take-time. Every
    // continuation below (close/refresh/reset-to-idle/toast) checks this
    // against the store's CURRENT pending request first: a second
    // `openUsageSidebar` call (a fresh save/delete opened elsewhere while
    // this one's write is still in flight) replaces `pending` outright, and
    // this request's eventual settling must never close, refresh, or
    // re-enable that LATER request's own sidebar.
    const myRequestId = taken.requestId;
    const isStillCurrent = () => useUsageSidebarStore.getState().pending?.requestId === myRequestId;

    runUsageSidebarDecision(
      taken,
      handlers,
      {
        fetchUsage,
        hasConflictedOnce: conflictedRef.current,
      },
      isStillCurrent,
    )
      .then((result) => {
        if (!isStillCurrent()) return;
        if (result.outcome === 'success') {
          conflictedRef.current = false;
          closeUsageSidebar();
        } else if (result.outcome === 'refetch-and-reopen') {
          conflictedRef.current = true;
          refreshUsageSidebarUsage(myRequestId, result.usage);
          resetUsageSidebarToIdle(myRequestId);
        } else if (result.outcome === 'terminal-conflict') {
          resetUsageSidebarToIdle(myRequestId);
          addErrorToast({ content: USAGE_SIDEBAR_CONFLICT_RETRY_MESSAGE });
        }
      })
      .catch((error) => {
        if (!isStillCurrent()) return;
        // R14 — a stale refusal parks the pending stale (the panel shows the
        // sentence, no toast, no reload); anything else resets it to idle
        // and toasts. See the helper's doc.
        const sentence = settleUsageSidebarFailure(error, myRequestId);
        if (sentence !== null) addErrorToast({ content: sentence });
      })
      .finally(() => {
        // D16 — only clear THIS request's own slot: a newer request may
        // already have taken over `runningRequestIdRef.current` by the time
        // this settles (possible once `openUsageSidebar`'s running-block is
        // bypassed by a legitimate idle→running transition on the SAME
        // pending object, e.g. a conflict retry's re-pick), and clobbering
        // that would wrongly mark it as "not running" mid-flight.
        if (runningRequestIdRef.current === myRequestId) runningRequestIdRef.current = null;
      });
    // `handlers` is a fresh object every render at every real call site — this
    // effect's own guards (`pending.decision === null` / `runningRef`) make a
    // re-run on an unrelated render a cheap no-op, so it is NOT a dependency
    // (`react-hooks/exhaustive-deps` is off repo-wide, `eslint.config.mjs:42`).
  }, [pending, originKey, apiFetch, queryClient]);
}
